import type { MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';
import { Mail, RefreshCw } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { ArchiveSeal } from '@/components/ArchiveSeal';
import { useAuthor } from '@/hooks/useAuthor';
import { useActiveSection } from '@/hooks/useActiveSection';
import { ContactLauncher } from '@/components/ContactLauncher';
import { ARCHIVE_PUBKEY } from '@/hooks/useArchiveNotes';
import { cn } from '@/lib/utils';

/** Reading progress through the current feed. */
export interface ArchiveBarProgress {
  /** Records revealed so far. */
  shown: number;
  /** Total records in the current view. */
  total: number;
}

interface ArchiveBarProps {
  /**
   * "fixed" pins the bar to the viewport and lets it slide in/out (used over
   * the home hero). "sticky" keeps it in the page flow and always visible
   * (used on record pages).
   */
  position?: 'fixed' | 'sticky';
  /** Only meaningful for position="fixed": whether the bar is shown. */
  visible?: boolean;
  /** Where the bar links to. Defaults to the archive's profile. */
  to?: string;
  /** Accessible label for the link. */
  label?: string;
  /** Optional click handler (e.g. to act like the browser's Back button). */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  /** When set, shows a "shown / total" readout and a progress line in the bar. */
  progress?: ArchiveBarProgress;
  /**
   * When set, shows jump links to these page sections, highlighting the current
   * one. An item with a `suffix` reads "label & suffix" as a single link; the
   * "&" is replaced by turning arrows while the archive is loading.
   */
  sections?: ReadonlyArray<{ id: string; label: string; suffix?: string }>;
  /**
   * When set (with `sections`), the progress meter only shows while this
   * section is the current one, so it never reports feed progress to someone
   * who is still reading the Directory.
   */
  progressSection?: string;
  /** Shows a Contact button that opens the About / contact panel. */
  showContact?: boolean;
  /**
   * True while the archive is still being gathered. A refresh arrow turns
   * beside the "Archive" link, so the reader can tell at a glance, wherever
   * they have scrolled to, that more records are still arriving.
   */
  scanning?: boolean;
  /**
   * When set, the loading arrows only show in the bar once the element with this
   * id has reached the top of the screen (the reader is into the records).
   */
  scanSection?: string;
  className?: string;
}

/**
 * A short form of a count for tight spaces: 169 stays 169, 1,240 becomes 1.2K,
 * 12,500 becomes 12.5K. Keeps the readout the same width however big the
 * archive gets.
 */
const COMPACT = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
function compactCount(n: number): string {
  return COMPACT.format(n);
}

/** Smooth-scroll to a section, unless the reader prefers reduced motion. */
function jumpTo(id: string) {
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
}

/**
 * The slim archive header bar: a small seal, the lowercase masthead title, and
 * the dateline. Hairline border, no shadow. Shared by the home page's scroll
 * header and by record pages, so both read as the same publication.
 *
 * On the home feed it also carries the reading meter: a count readout on the
 * right and a thin line along the bottom edge that fills as more is revealed.
 */
export function ArchiveBar({
  position = 'sticky',
  visible = true,
  to,
  label = 'Archive profile',
  onClick,
  progress,
  sections,
  progressSection,
  showContact = false,
  scanning = false,
  scanSection,
  className,
}: ArchiveBarProps) {
  const activeSection = useActiveSection(sections?.map((s) => s.id) ?? []);
  const meterOn = !progressSection || activeSection === progressSection;
  // The turning arrows show whenever the bar is up and records are loading.
  // (If `scanSection` is given, they wait until that section reaches the top.)
  const inRecords = useActiveSection(scanSection ? [scanSection] : []) === scanSection;
  const scanningHere = scanning && (!scanSection || inRecords);
  const author = useAuthor(ARCHIVE_PUBKEY);
  const metadata = author.data?.metadata;
  const npub = nip19.npubEncode(ARCHIVE_PUBKEY);
  const title = metadata?.display_name ?? metadata?.name ?? 'Anonymous Archive';
  const shown = position === 'sticky' || visible;

  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.shown / progress.total) * 100))
    : 0;

  return (
    <div
      aria-hidden={!shown}
      className={cn(
        'inset-x-0 top-0 z-50 border-b border-foreground/20 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75',
        position === 'fixed'
          ? cn(
              'fixed transition-[opacity,transform] duration-300 ease-out',
              shown ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0',
            )
          : 'sticky',
        className,
      )}
    >
      <div className="relative mx-auto flex h-12 max-w-6xl items-center gap-2 px-4 sm:gap-3">
        {/* Announces the loading state to screen readers (the arrow is visual). */}
        {scanningHere && (
          <span role="status" className="sr-only">
            Loading records
          </span>
        )}
        <Link
          to={to ?? `/${npub}`}
          onClick={onClick}
          tabIndex={shown ? 0 : -1}
          aria-label={label}
          className={cn(
            'flex items-center gap-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            // With section links present the title is hidden on phones, so this
            // link is just the seal: it must never be squeezed narrower than the
            // seal (that is what let "Archive & Directory" slide over it). From
            // tablet up it is the flexible part of the bar again.
            sections ? 'shrink-0 sm:min-w-0 sm:flex-1' : 'min-w-0 flex-1',
          )}
        >
          <ArchiveSeal
            picture={metadata?.picture}
            name={metadata?.name}
            loading={author.isLoading}
            size={30}
            borderWidth={1.5}
            className="shrink-0"
          />
          {/* On phones the seal stands in for the title when section links
              are present, so everything fits on one line. */}
          <span
            className={cn(
              'truncate font-masthead text-xl leading-none text-foreground',
              sections && 'hidden sm:inline',
            )}
          >
            {title}
          </span>
          {!progress && !sections && (
            <span className="ml-auto hidden font-engraved text-[10px] font-medium uppercase tracking-[0.3em] text-foreground/60 sm:block">
              Archive &amp; Directory
            </span>
          )}
        </Link>

        {sections && (
          <nav aria-label="Sections" className="flex min-w-0 items-center gap-3 sm:shrink-0 sm:gap-8">
            {sections.map((s) => {
              const current = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => jumpTo(s.id)}
                  tabIndex={shown ? 0 : -1}
                  aria-current={current ? 'location' : undefined}
                  aria-label={s.suffix ? `${s.label} and ${s.suffix}` : undefined}
                  className={cn(
                    // Tighter type and spacing on phones, so "Archive & Directory"
                    // fits beside the seal and Contact on a 360px screen.
                    'relative inline-flex items-center gap-1 whitespace-nowrap py-4 font-engraved text-[9px] font-semibold uppercase tracking-[0.1em] transition-colors sm:gap-1.5 sm:text-[11px] sm:tracking-[0.28em]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    current ? 'text-foreground' : 'text-foreground/55 hover:text-foreground',
                  )}
                >
                  {s.label}

                  {s.suffix && (
                    <>
                      {/* The connector: an "&" that gives way to turning arrows
                          while records are still arriving, and comes back when
                          loading is done. Both live in one fixed-size slot,
                          stacked, so swapping never moves the words or the links
                          beside them. */}
                      <span
                        aria-hidden="true"
                        className="relative inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center"
                      >
                        <span
                          className={cn(
                            'absolute inset-0 flex items-center justify-center transition-opacity duration-200 motion-reduce:transition-none',
                            scanningHere ? 'opacity-0' : 'opacity-100',
                          )}
                        >
                          &amp;
                        </span>
                        <RefreshCw
                          className={cn(
                            'absolute h-3 w-3 transition-opacity duration-200 motion-reduce:transition-none',
                            scanningHere ? 'opacity-100 motion-safe:animate-spin' : 'opacity-0',
                          )}
                        />
                      </span>
                      {s.suffix}
                    </>
                  )}
                </button>
              );
            })}
          </nav>
        )}

        {/* Our team — a small letter with the words inside it. Opens the About
            + get-in-touch panel. Always in reach, so a visitor never has to hunt
            for a way to contact the archivists. */}
        {showContact && (
          <ContactLauncher>
            {(openContact) => (
              <button
                type="button"
                onClick={openContact}
                tabIndex={shown ? 0 : -1}
                aria-label="Our team — contact the archive"
                className="group relative my-2 inline-flex shrink-0 items-center gap-1.5 rounded-[3px] border border-foreground/40 px-1.5 py-1 font-engraved text-[9px] font-semibold uppercase tracking-[0.14em] text-foreground/70 transition-colors [-webkit-tap-highlight-color:transparent] hover:border-foreground hover:bg-foreground hover:text-background active:border-foreground active:bg-foreground active:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:px-2.5 sm:text-[10px] sm:tracking-[0.2em]"
              >
                <Mail aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                {/* On the narrowest phones the envelope stands alone; the words
                    return once there is room (its label is still read aloud). */}
                <span className="hidden min-[400px]:inline">Our team</span>
              </button>
            )}
          </ContactLauncher>
        )}

        {progress && progress.total > 0 && (
          <div
            aria-hidden={!meterOn}
            className={cn(
              'flex shrink-0 items-center gap-2.5 transition-opacity duration-300 motion-reduce:transition-none',
              !meterOn && 'pointer-events-none opacity-0',
            )}
          >
            <span
              className={cn(
                'whitespace-nowrap font-typewriter text-[10px] uppercase tracking-[0.15em] text-muted-foreground',
                // Shows from 380px up, as before. Its letter-spacing is tighter
                // on phones, and the numbers are abbreviated there (see below),
                // so it stays short however large the archive grows.
                'tracking-[0.04em] md:tracking-[0.15em]',
                sections && 'hidden min-[380px]:inline',
              )}
            >
              <span className="font-bold tabular-nums text-foreground">
                <span className="md:hidden">{compactCount(progress.shown)}</span>
                <span className="hidden md:inline">{progress.shown.toLocaleString()}</span>
              </span>
              <span className="mx-0.5 opacity-40 md:mx-1">/</span>
              <span className="tabular-nums">
                <span className="md:hidden">{compactCount(progress.total)}</span>
                <span className="hidden md:inline">{progress.total.toLocaleString()}</span>
              </span>
            </span>
            <span
              className={cn(
                'flex h-6 min-w-[2.5rem] items-center justify-center rounded-sm bg-foreground px-1.5 font-typewriter text-[10px] font-bold tabular-nums text-background',
                // On the narrowest phones the % badge gives way (the progress
                // line along the bar's bottom edge still shows the same thing).
                sections && 'hidden min-[400px]:flex',
              )}
            >
              {pct}%
            </span>
          </div>
        )}
      </div>

      {/* Progress line — rides on the bar's bottom hairline, full width. */}
      {progress && progress.total > 0 && (
        <div
          role="progressbar"
          aria-label="Records loaded"
          aria-hidden={!meterOn}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          className={cn(
            'absolute inset-x-0 -bottom-px h-[2px] transition-opacity duration-300 motion-reduce:transition-none',
            !meterOn && 'opacity-0',
          )}
        >
          <div
            className="h-full bg-flag-red transition-[width] duration-500 ease-out motion-reduce:transition-none"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
